import { defineComponent as _defineComponent } from 'vue'
import { openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, Transition as _Transition, withCtx as _withCtx, createVNode as _createVNode, toDisplayString as _toDisplayString, normalizeClass as _normalizeClass, createElementBlock as _createElementBlock, createElementVNode as _createElementVNode, unref as _unref, renderList as _renderList, Fragment as _Fragment, resolveDynamicComponent as _resolveDynamicComponent, renderSlot as _renderSlot, normalizeStyle as _normalizeStyle } from "vue"

const _hoisted_1 = { class: "bg-racq-anti-flash-white flex flex-col grow min-h-[calc(100svh-56px-376px)] md:min-h-[calc(100svh-68px-376px)] lg:min-h-[calc(100svh-69px-199px)]" }
const _hoisted_2 = { class: "px-4 pt-6 pb-5 bg-white lg:hidden" }
const _hoisted_3 = {
  key: 0,
  class: /*@__PURE__*/_normalizeClass(`md:hidden text-racq-navy m-0 text-xl2-bold`)
}
const _hoisted_4 = {
  id: "base-form-container",
  class: "mx-0 md:mx-auto mb-12 md:mb-14 md:max-w-[784px] w-full sm:mt-0 md:mt-8 lg:mt-14"
}
const _hoisted_5 = { key: 0 }
const _hoisted_6 = {
  key: 1,
  class: "relative"
}
const _hoisted_7 = ["id", "dataStepName", "data-step-active"]
const _hoisted_8 = { class: "mx-0 md:mx-auto -mt-5 px-4 lg:px-0 max-w-[784px] w-full" }

import { computed, onMounted, ref, getCurrentInstance, watch } from 'vue';
import { ComponentFields, RichText } from '@sitecore-jss/sitecore-jss-vue';
import { FormRenderingData } from '@/interfaces/pages/sitecore-props.interface';

import FormStepper from '@/components/FormStepper/FormStepper.vue';
import FormStepperBar from '@/components/FormStepperBar/FormStepperBar.vue';
import FormStep from '@/components/FormStep/FormStep.vue';
import JeopardyV2 from '@/components/JeopardyV2/JeopardyV2.vue';
import ModalDialog from '@/components/ModalDialog/ModalDialog.vue';
import FormStepDisclaimers from '@/components/FormStepDisclaimers/FormStepDisclaimers.vue';
import FullScreenSpinner from '@/components/FullScreenSpinner/FullScreenSpinner.vue';

import { IFormStep } from '@/interfaces';
import { useJeopardyStore, useFormStore, useLiveChatStore } from '@/store';

// Define props and state
// ------------------------------------------------------------- //

export default /*@__PURE__*/_defineComponent({
  __name: 'BaseFormContainer',
  props: {
    rendering: {},
    fields: {},
    steps: {},
    floatBoxMounted: { type: Boolean }
  },
  setup(__props: any) {



const floatBoxTranslate = ref<{ x: number; y: number }>({ x: 0, y: 0 });
const showTooltipModal = ref(false);
const tooltipModalTitle = ref('');
const tooltipModalContent = ref('');

// Initialise stores
// ------------------------------------------------------------- //
const jpStore = useJeopardyStore();
const formStore = useFormStore();
const liveChatStore = useLiveChatStore();

// Computed Properties
// ------------------------------------------------------------- //
const showJeopardy = computed(() => jpStore.showJeopardy);
const prelimSteps = computed(() => formStore.prelimSteps);
const prelimStepIndex = computed(() => formStore.currentPrelimStepIndex);
const allPrelimStepsComplete = computed(() => formStore.allPrelimStepsComplete);
const pageTitle = computed(() => formStore.pageTitle);
const isStepperVisible = computed(() => formStore.isStepperVisible.value);
const activeStep = computed(() =>
  formStore.allPrelimStepsComplete ? formStore.currentStep : formStore.currentPrelimStep
);
const fullScreenLoading = computed(() => formStore.fullScreenLoading);
const showFloatingBox = computed(() => formStore.currentStep?.showIQFloatBox);
const isDisclaimerVisible = computed(() => formStore.isStepDisclaimerVisible);

const app = getCurrentInstance();
const jpFields = app?.appContext.config.globalProperties.$jss.routeData().placeholders['jss-jeopardy'][0].fields;

onMounted(() => {
  // Set the default Jeopardy to display for this form
  jpStore.setDefaultJeopardyPanel(jpFields);
  liveChatStore.setChatEnabled(true);
});

const updateFloatBox = () => {
  const { currentStep, currentStepIndex, inScopeSteps } = formStore;
  const paddingBetweenStepsInPixel = 20;

  if (!currentStep?.id) return;

  const timer = setInterval(() => {
    const getStepContainer = (stepId: string | undefined, stepIndex: number) =>
      stepId ? document.querySelector(`#${stepId + stepIndex.toString()} > div`) : null;

    // Get all previous step containers and accumulate their heights
    let totalHeight = 0;
    for (let i = 0; i < currentStepIndex; i++) {
      const stepId = inScopeSteps[i]?.id;

      const stepContainer = getStepContainer(stepId, i);
      if (stepContainer) {
        totalHeight += stepContainer.clientHeight + paddingBetweenStepsInPixel;
      }
    }

    const currentStepContainer = getStepContainer(currentStep.id, currentStepIndex);

    if (currentStepContainer) {
      clearInterval(timer);
      // Adjust y position with the accumulated height of previous steps
      floatBoxTranslate.value = {
        x: currentStepContainer.clientWidth + 30,
        y: totalHeight,
      };
    }
  }, 100);
};

/**
 * Add as global function to be able to access by tooltip plugin
 */
(window as any).showRACQTooltipModal = (title: string, content: string) => {
  showTooltipModal.value = true;
  tooltipModalTitle.value = title;
  tooltipModalContent.value = content;
};

(window as any).hideRACQTooltipModal = () => {
  showTooltipModal.value = false;
};

watch(
  () => formStore.currentStep, // The property to watch
  (newStep, oldStep) => {
    if (newStep !== oldStep) {
      updateFloatBox(); // Trigger the function when `currentStep` changes
    }
  },
  { immediate: true } // Trigger the watcher immediately after the component mounts
);

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock(_Fragment, null, [
    _createElementVNode("main", _hoisted_1, [
      _createVNode(_Transition, null, {
        default: _withCtx(() => [
          (fullScreenLoading.value)
            ? (_openBlock(), _createBlock(FullScreenSpinner, { key: 0 }))
            : _createCommentVNode("", true)
        ]),
        _: 1
      }),
      _createElementVNode("div", _hoisted_2, [
        (pageTitle.value)
          ? (_openBlock(), _createElementBlock("h3", _hoisted_3, _toDisplayString(pageTitle.value), 1))
          : _createCommentVNode("", true),
        (isStepperVisible.value)
          ? (_openBlock(), _createBlock(FormStepperBar, {
              key: 1,
              class: "mt-2 h-6 w-full justify-stretch items-center"
            }))
          : _createCommentVNode("", true)
      ]),
      _createElementVNode("div", _hoisted_4, [
        (isStepperVisible.value && allPrelimStepsComplete.value)
          ? (_openBlock(), _createBlock(FormStepper, {
              key: 0,
              steps: _ctx.steps,
              currentStepIndex: _unref(formStore).currentStepIndex
            }, null, 8, ["steps", "currentStepIndex"]))
          : _createCommentVNode("", true),
        _createVNode(_Transition, {
          name: "component-fade",
          mode: "out-in"
        }, {
          default: _withCtx(() => [
            (!allPrelimStepsComplete.value && !showJeopardy.value)
              ? (_openBlock(), _createElementBlock("div", { key: prelimStepIndex.value }, [
                  (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(prelimSteps.value, (prelimStep, index) => {
                    return (_openBlock(), _createElementBlock(_Fragment, {
                      key: prelimStep.id
                    }, [
                      (index === prelimStepIndex.value)
                        ? (_openBlock(), _createElementBlock("div", _hoisted_5, [
                            _createVNode(FormStep, { step: prelimStep }, {
                              default: _withCtx(() => [
                                (_openBlock(), _createBlock(_resolveDynamicComponent(prelimStep.component), { step: prelimStep }, null, 8, ["step"]))
                              ]),
                              _: 2
                            }, 1032, ["step"])
                          ]))
                        : _createCommentVNode("", true)
                    ], 64))
                  }), 128))
                ]))
              : (allPrelimStepsComplete.value)
                ? (_openBlock(), _createElementBlock("div", _hoisted_6, [
                    (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_ctx.steps, (step, index) => {
                      return (_openBlock(), _createElementBlock("section", {
                        id: step.id + index.toString(),
                        key: index,
                        class: _normalizeClass([
              'js-scrollspy-form-step',
              {
                '!hidden md:!flex': index != _unref(formStore).currentStepIndex && step.isVisible,
                '!hidden': !step.isVisible,
              },
              step.isActive ? 'z-[1]' : 'z-[0]',
            ]),
                        dataStepName: step.stepGroupName || step.stepTitle,
                        "data-step-active": step.isActive
                      }, [
                        _createVNode(FormStep, {
                          step: step,
                          stepIndex: index + 1
                        }, {
                          default: _withCtx(() => [
                            (step.isVisible)
                              ? (_openBlock(), _createBlock(_resolveDynamicComponent(step.component), {
                                  key: 0,
                                  step: step
                                }, null, 8, ["step"]))
                              : _createCommentVNode("", true)
                          ]),
                          _: 2
                        }, 1032, ["step", "stepIndex"])
                      ], 10, _hoisted_7))
                    }), 128)),
                    (allPrelimStepsComplete.value && _ctx.floatBoxMounted && showFloatingBox.value)
                      ? (_openBlock(), _createElementBlock("div", {
                          key: 0,
                          class: "top-0 w-full xl:w-64 xl:absolute max-xl:!transform-none",
                          style: _normalizeStyle({
              transform: `translate(${floatBoxTranslate.value.x}px, ${floatBoxTranslate.value.y}px)`,
            })
                        }, [
                          _renderSlot(_ctx.$slots, "floatBox")
                        ], 4))
                      : _createCommentVNode("", true)
                  ]))
                : _createCommentVNode("", true)
          ]),
          _: 3
        }),
        (!showJeopardy.value)
          ? _renderSlot(_ctx.$slots, "bottomBox", { key: 1 })
          : _createCommentVNode("", true),
        _createVNode(JeopardyV2, { fields: _unref(jpFields) }, null, 8, ["fields"])
      ]),
      _createElementVNode("div", _hoisted_8, [
        (isDisclaimerVisible.value)
          ? (_openBlock(), _createBlock(FormStepDisclaimers, {
              key: 0,
              fields: {
          defaultDisclaimer: activeStep.value?.stepDisclaimer,
          stepTitle: activeStep.value?.stepTitle,
        }
            }, null, 8, ["fields"]))
          : _createCommentVNode("", true)
      ])
    ]),
    _createVNode(ModalDialog, {
      modelValue: showTooltipModal.value,
      "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((showTooltipModal).value = $event)),
      closeOnEsc: true,
      showXCloseButton: true,
      size: "tooltip"
    }, {
      default: _withCtx(() => [
        (tooltipModalTitle.value)
          ? (_openBlock(), _createBlock(_unref(RichText), {
              key: 0,
              field: { value: tooltipModalTitle.value },
              class: "sticky-modal-header"
            }, null, 8, ["field"]))
          : _createCommentVNode("", true),
        _createVNode(_unref(RichText), {
          field: { value: tooltipModalContent.value },
          class: "tooltip-body richtext"
        }, null, 8, ["field"])
      ]),
      _: 1
    }, 8, ["modelValue"])
  ], 64))
}
}

})